
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  Loader2, 
  RotateCcw, 
  Database, 
  User, 
  Calendar, 
  FileText, 
  Filter,
  Search,
  RefreshCw,
  Eye,
  AlertTriangle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface ImportLog {
  id: string;
  user_name: string;
  table_name: string;
  operation_type: string;
  records_affected: number;
  import_timestamp: string;
  rollback_data: any;
  status: string;
  description?: string;
  old_data?: any;
  new_data?: any;
  changed_fields?: string[];
  operation_context?: string;
}

export function ImportAuditLog() {
  const [importLogs, setImportLogs] = useState<ImportLog[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ImportLog[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<ImportLog | null>(null);
  const [detailsLog, setDetailsLog] = useState<ImportLog | null>(null);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterTable, setFilterTable] = useState<string>("all");
  const { toast } = useToast();

  const fetchImportLogs = async () => {
    try {
      setIsLoading(true);
      const { data: rawData, error } = await supabase
        .from('import_audit_logs')
        .select('*')
        .order('import_timestamp', { ascending: false })
        .limit(200);
        
      if (error) {
        console.error("Error fetching import logs:", error);
        toast({
          title: "Error fetching import logs",
          description: error.message,
          variant: "destructive"
        });
        return;
      }
      
      if (rawData) {
        const importLogsData = rawData.map(log => ({
          id: log.id,
          user_name: log.user_name,
          table_name: log.table_name,
          operation_type: log.operation_type,
          records_affected: log.records_affected,
          import_timestamp: log.import_timestamp,
          rollback_data: log.rollback_data,
          status: log.status,
          description: log.description,
          old_data: log.old_data,
          new_data: log.new_data,
          changed_fields: log.changed_fields,
          operation_context: log.operation_context
        })) as ImportLog[];
        
        setImportLogs(importLogsData);
        setFilteredLogs(importLogsData);
      }
    } catch (err) {
      console.error("Failed to fetch import logs:", err);
      toast({
        title: "Unexpected error",
        description: "Failed to load import logs",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Set up real-time updates
  useEffect(() => {
    fetchImportLogs();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('audit-logs-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'import_audit_logs'
        },
        (payload) => {
          console.log('Real-time audit log update:', payload);
          fetchImportLogs(); // Refresh the list when changes occur
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Filter logs based on search term, status, and table
  useEffect(() => {
    let filtered = importLogs;
    
    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.table_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.operation_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.operation_context?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterStatus !== "all") {
      filtered = filtered.filter(log => log.status === filterStatus);
    }

    if (filterTable !== "all") {
      filtered = filtered.filter(log => log.table_name === filterTable);
    }
    
    setFilteredLogs(filtered);
  }, [searchTerm, filterStatus, filterTable, importLogs]);

  const handleRollback = async (log: ImportLog) => {
    try {
      setIsRollingBack(true);
      
      const { data, error } = await supabase.functions.invoke('rollback-operation', {
        body: JSON.stringify({
          logId: log.id
        })
      });
      
      if (error) {
        throw new Error(error.message);
      }
      
      if (!data.success) {
        throw new Error(data.error || 'Rollback failed');
      }
      
      toast({
        title: "Rollback successful",
        description: data.message || `Successfully rolled back operation on ${log.table_name}`,
      });
      
      setSelectedLog(null);
      fetchImportLogs();
      
    } catch (err: any) {
      console.error("Rollback failed:", err);
      toast({
        title: "Rollback failed",
        description: err.message || "Failed to rollback operation",
        variant: "destructive"
      });
    } finally {
      setIsRollingBack(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-600 text-white">Completed</Badge>;
      case 'failed':
        return <Badge className="bg-red-600 text-white">Failed</Badge>;
      case 'rolled_back':
        return <Badge variant="destructive">Rolled Back</Badge>;
      default:
        return <Badge className="bg-blue-600 text-white">{status}</Badge>;
    }
  };

  const getOperationBadge = (operation: string) => {
    const colors = {
      insert: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300",
      update: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300", 
      delete: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
    };
    
    return (
      <Badge className={colors[operation as keyof typeof colors] || "bg-gray-100 text-gray-800"}>
        {operation.toUpperCase()}
      </Badge>
    );
  };

  const getUniqueTableNames = () => {
    return [...new Set(importLogs.map(log => log.table_name))].sort();
  };

  const canRollback = (log: ImportLog) => {
    return log.status === 'completed' && (log.old_data || log.new_data);
  };

  return (
    <Card className="dashboard-card bg-transparent shadow-none border-2">
      <CardHeader className="border-b border-dashboard-border pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Database className="h-5 w-5" />
              Import Audit Log & Rollback
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Real-time tracking of all database operations with comprehensive rollback capabilities
            </p>
          </div>
          <Button
            onClick={fetchImportLogs}
            disabled={isLoading}
            variant="outline"
            className="border-primary text-primary hover:bg-primary hover:text-white"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-6">
        {/* Enhanced Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by user, table, operation, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="border rounded-md px-3 py-2 bg-background min-w-[120px]"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="rolled_back">Rolled Back</option>
            </select>
            <select
              value={filterTable}
              onChange={(e) => setFilterTable(e.target.value)}
              className="border rounded-md px-3 py-2 bg-background min-w-[140px]"
            >
              <option value="all">All Tables</option>
              {getUniqueTableNames().map(tableName => (
                <option key={tableName} value={tableName}>{tableName}</option>
              ))}
            </select>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <Table>
              <TableHeader className="bg-gray-50 dark:bg-gray-800">
                <TableRow>
                  <TableHead className="w-[120px]">
                    <div className="flex items-center gap-1">
                      <User className="h-4 w-4" />
                      User
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Database className="h-4 w-4" />
                      Table
                    </div>
                  </TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <FileText className="h-4 w-4" />
                      Operation
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Records</TableHead>
                  <TableHead>
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Timestamp
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="bg-white dark:bg-gray-800/50">
                {filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <div className="flex flex-col items-center gap-2">
                        <Database className="h-12 w-12 text-gray-300" />
                        <p className="text-lg font-medium">No audit logs found</p>
                        <p className="text-sm">
                          {searchTerm || filterStatus !== "all" || filterTable !== "all"
                            ? "Try adjusting your search or filter criteria" 
                            : "Database operations will appear here automatically"
                          }
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <TableCell className="font-medium">{log.user_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {log.table_name}
                        </Badge>
                      </TableCell>
                      <TableCell>{getOperationBadge(log.operation_type)}</TableCell>
                      <TableCell className="text-center font-mono">
                        {log.records_affected.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-sm">
                        {new Date(log.import_timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(log.status)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setDetailsLog(log)}
                            className="h-8 px-3 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Details
                          </Button>
                          {canRollback(log) && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setSelectedLog(log)}
                              className="h-8 px-3 text-xs"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Rollback
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Operation Details Dialog */}
        <Dialog open={!!detailsLog} onOpenChange={() => setDetailsLog(null)}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Operation Details</DialogTitle>
              <DialogDescription>
                Detailed information about the database operation
              </DialogDescription>
            </DialogHeader>
            {detailsLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">User:</label>
                    <p className="text-sm">{detailsLog.user_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Table:</label>
                    <p className="text-sm">{detailsLog.table_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Operation:</label>
                    <p className="text-sm">{detailsLog.operation_type.toUpperCase()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Records Affected:</label>
                    <p className="text-sm">{detailsLog.records_affected}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Timestamp:</label>
                    <p className="text-sm">{new Date(detailsLog.import_timestamp).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status:</label>
                    <p className="text-sm">{detailsLog.status}</p>
                  </div>
                </div>
                
                {detailsLog.changed_fields && detailsLog.changed_fields.length > 0 && (
                  <div>
                    <label className="text-sm font-medium">Changed Fields:</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailsLog.changed_fields.map(field => (
                        <Badge key={field} variant="secondary" className="text-xs">{field}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {detailsLog.description && (
                  <div>
                    <label className="text-sm font-medium">Description:</label>
                    <p className="text-sm">{detailsLog.description}</p>
                  </div>
                )}

                {detailsLog.operation_context && (
                  <div>
                    <label className="text-sm font-medium">Context:</label>
                    <p className="text-sm">{detailsLog.operation_context}</p>
                  </div>
                )}

                {detailsLog.old_data && (
                  <div>
                    <label className="text-sm font-medium">Previous Data:</label>
                    <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded max-h-32 overflow-auto">
                      {JSON.stringify(detailsLog.old_data, null, 2)}
                    </pre>
                  </div>
                )}

                {detailsLog.new_data && (
                  <div>
                    <label className="text-sm font-medium">New Data:</label>
                    <pre className="text-xs bg-gray-100 dark:bg-gray-800 p-2 rounded max-h-32 overflow-auto">
                      {JSON.stringify(detailsLog.new_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Rollback Confirmation Dialog */}
        <AlertDialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Confirm Rollback Operation
              </AlertDialogTitle>
              <AlertDialogDescription className="space-y-3">
                <p>
                  Are you sure you want to rollback the <strong>{selectedLog?.operation_type}</strong> operation 
                  from <strong>{selectedLog?.import_timestamp && new Date(selectedLog.import_timestamp).toLocaleString()}</strong>?
                </p>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">This action will:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                    <li>
                      {selectedLog?.operation_type === 'insert' && 'Delete the inserted record'}
                      {selectedLog?.operation_type === 'update' && 'Restore the previous values'}
                      {selectedLog?.operation_type === 'delete' && 'Restore the deleted record'}
                    </li>
                    <li>Affect records in the <strong>{selectedLog?.table_name}</strong> table</li>
                    <li className="font-semibold text-red-600 dark:text-red-400">This rollback cannot be undone</li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => selectedLog && handleRollback(selectedLog)}
                className="bg-red-600 hover:bg-red-700"
                disabled={isRollingBack}
              >
                {isRollingBack ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Rolling back...
                  </>
                ) : (
                  <>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Confirm Rollback
                  </>
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
